// ==========================================
// Buddy host — placement, drag/snap, resize, wake (3d LAYOUT & PLACEMENT, 4a, 4c)
// ==========================================
// The only Buddy surface the app shell mounts. It owns everything the desktop spec
// delegates to the OS:
//
//   frameless always-on-top window  ->  position: fixed at z-index 900
//   drag by the title bar, snap     ->  pointer drag, snaps to the nearest edge
//   56px bubble, drag to any corner ->  pointer drag, snaps to the nearest corner
//   Option/Alt+Space global hotkey  ->  document-scoped keydown
//   resizable 260-420 x 400-900     ->  a corner handle, clamped to that range
//
// z-index 900 keeps Buddy under Angular Material dialogs (1000) so ERP modals still
// open cleanly over it.
// ==========================================

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  BbState,
  BubbleCorner,
  BuddyLayout,
  BuddyPlacement,
  BuddyProgress,
  Density,
  PanelEdge,
} from '../models/buddy.types';
import { BUDDY_Z_INDEX, BUBBLE_SIZE, PANEL_INSET } from '../models/buddy.constants';
import { BbStateService } from '../services/bb-state.service';
import { BuddyChatService } from '../services/buddy-chat.service';
import { BuddyProgressService } from '../services/buddy-progress.service';
import { BuddyStateService } from '../services/buddy-state.service';

/** Keeps the top-anchored corners clear of the ERP header. */
const TOP_INSET = 72;
/** Movement past this many px counts as a drag rather than a click. */
const DRAG_THRESHOLD = 4;

interface Point {
  x: number;
  y: number;
}

@Component({
  selector: 'app-buddy-host',
  standalone: false,
  templateUrl: './buddy-host.component.html',
  styleUrls: ['./buddy-host.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BuddyHostComponent implements OnInit, OnDestroy {
  readonly zIndex = BUDDY_Z_INDEX;

  placement!: BuddyPlacement;
  density: Density = 'default';
  panelOpen = false;
  useEdgeTab = false;
  /** 3a stacked panel, or 1c's quest rail. */
  layout: BuddyLayout = 'panel';
  bbState: BbState = 'dozing';
  progress!: BuddyProgress;

  /** Free position while a drag is in flight; null means "snapped". */
  dragPos: Point | null = null;
  dragging = false;
  /** Drives the 4c grow-from-the-bubble animation on the first frame after opening. */
  waking = false;

  private readonly destroyed$ = new Subject<void>();

  private dragOrigin: Point | null = null;
  private dragOffset: Point = { x: 0, y: 0 };
  private dragTarget: 'bubble' | 'panel' | null = null;
  private resizeStart: { x: number; y: number; width: number; height: number } | null = null;
  private wakeTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly onPointerMove = (event: PointerEvent): void => this.handlePointerMove(event);
  private readonly onPointerUp = (event: PointerEvent): void => this.handlePointerUp(event);

  constructor(
    private readonly shell: BuddyStateService,
    private readonly bb: BbStateService,
    private readonly progressService: BuddyProgressService,
    private readonly chat: BuddyChatService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.placement = this.shell.currentPlacement;
    this.progress = this.progressService.current;
  }

  ngOnInit(): void {
    this.shell.placement.pipe(takeUntil(this.destroyed$)).subscribe((p) => {
      this.placement = p;
      this.cdr.markForCheck();
    });
    this.shell.density.pipe(takeUntil(this.destroyed$)).subscribe((d) => {
      this.density = d;
      this.cdr.markForCheck();
    });
    this.shell.surface.pipe(takeUntil(this.destroyed$)).subscribe((s) => {
      this.panelOpen = s === 'panel';
      this.cdr.markForCheck();
    });
    this.shell.useEdgeTab.pipe(takeUntil(this.destroyed$)).subscribe((v) => {
      this.useEdgeTab = v;
      this.cdr.markForCheck();
    });
    this.shell.layout.pipe(takeUntil(this.destroyed$)).subscribe((l) => {
      this.layout = l;
      this.cdr.markForCheck();
    });
    this.bb.state.pipe(takeUntil(this.destroyed$)).subscribe((s) => {
      this.bbState = s;
      this.cdr.markForCheck();
    });
    this.progressService.progress.pipe(takeUntil(this.destroyed$)).subscribe((p) => {
      this.progress = p;
      this.cdr.markForCheck();
    });
  }

  // ------------------------------------------
  // Derived layout
  // ------------------------------------------

  get xpPct(): number {
    if (!this.progress.xpForLevel) {
      return 0;
    }
    return Math.min(100, Math.round((this.progress.xpIntoLevel / this.progress.xpForLevel) * 100));
  }

  /** Bubble position: free while dragging, otherwise pinned to its corner. */
  get bubbleStyle(): Record<string, string> {
    if (this.dragPos) {
      return { left: `${this.dragPos.x}px`, top: `${this.dragPos.y}px` };
    }
    const c = this.placement.corner;
    const style: Record<string, string> = {};
    if (c === 'bottom-left' || c === 'bottom-right') {
      style['bottom'] = `${PANEL_INSET}px`;
    } else {
      style['top'] = `${TOP_INSET}px`;
    }
    if (c === 'bottom-left' || c === 'top-left') {
      style['left'] = `${PANEL_INSET}px`;
    } else {
      style['right'] = `${PANEL_INSET}px`;
    }
    return style;
  }

  get panelStyle(): Record<string, string> {
    const style: Record<string, string> = {
      width: `${this.placement.width}px`,
      height: `${this.placement.height}px`,
      bottom: `${PANEL_INSET}px`,
      // 4c: the panel grows out of the bubble's corner, so the origin follows it.
      transformOrigin: this.placement.corner.replace('-', ' '),
    };
    if (this.dragPos) {
      style['left'] = `${this.dragPos.x}px`;
      style['top'] = `${this.dragPos.y}px`;
      delete style['bottom'];
    } else if (this.placement.edge === 'left') {
      style['left'] = `${PANEL_INSET}px`;
    } else {
      style['right'] = `${PANEL_INSET}px`;
    }
    return style;
  }

  get edgeTabStyle(): Record<string, string> {
    const style: Record<string, string> = { top: '50%', transform: 'translateY(-50%)' };
    if (this.placement.edge === 'left') {
      style['left'] = '0';
    } else {
      style['right'] = '0';
    }
    return style;
  }

  /** Something worth surfacing while collapsed — drives the bubble's pulse. */
  get hasAttention(): boolean {
    return this.progress.quest.done < this.progress.quest.total;
  }

  get pendingCount(): number {
    return Math.max(0, this.progress.quest.total - this.progress.quest.done);
  }

  // ------------------------------------------
  // Open / close
  // ------------------------------------------

  openPanel(): void {
    this.shell.open();
    this.bb.wake();

    // First open of the day pays +15 and rolls the streak forward.
    const award = this.progressService.awardDailyOpen();
    if (award) {
      this.chat.appendMilestone(
        `Day ${this.progressService.current.streakDays} — ${this.progressService.current.multiplier}× XP while the streak holds.`
      );
      if (award.leveledUp) {
        this.bb.celebrate();
      }
    }

    // 4c: spring in over 220ms, anchored to the bubble's corner.
    this.waking = true;
    this.clearWakeTimer();
    this.wakeTimer = setTimeout(() => {
      this.waking = false;
      this.wakeTimer = null;
      this.cdr.markForCheck();
    }, 260);
  }

  closePanel(): void {
    this.shell.close();
    // BB returns to dozing on his own 5-minute timer once things go quiet.
    this.bb.noteActivity();
  }

  toggleEdge(): void {
    this.shell.setEdge(this.placement.edge === 'right' ? 'left' : 'right');
  }

  /** Swap between 3a's stacked panel and 1c's quest rail. */
  toggleLayout(): void {
    this.shell.toggleLayout();
  }

  // ------------------------------------------
  // Keyboard — the document-scoped stand-in for the OS hotkey
  // ------------------------------------------

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    // Alt+Space (Option+Space on macOS) toggles Buddy.
    if (event.altKey && (event.code === 'Space' || event.key === ' ')) {
      event.preventDefault();
      if (this.panelOpen) {
        this.closePanel();
      } else {
        this.openPanel();
      }
      return;
    }

    // Cmd/Ctrl+K opens the workflow palette while the panel is up.
    if (this.panelOpen && (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.shell.togglePalette();
      return;
    }

    // Cmd/Ctrl+J switches threads — the shortcut 3d specifies.
    if (this.panelOpen && (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'j') {
      event.preventDefault();
      this.shell.toggleHistory();
    }
  }

  // ------------------------------------------
  // Drag — bubble to a corner, panel to an edge
  // ------------------------------------------

  startBubbleDrag(event: PointerEvent): void {
    this.beginDrag(event, 'bubble');
  }

  startPanelDrag(event: PointerEvent): void {
    this.beginDrag(event, 'panel');
  }

  private beginDrag(event: PointerEvent, target: 'bubble' | 'panel'): void {
    const host = (event.currentTarget as HTMLElement | null) ?? null;
    const rect = host?.getBoundingClientRect();
    this.dragTarget = target;
    this.dragOrigin = { x: event.clientX, y: event.clientY };
    this.dragOffset = rect
      ? { x: event.clientX - rect.left, y: event.clientY - rect.top }
      : { x: 0, y: 0 };

    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
  }

  private handlePointerMove(event: PointerEvent): void {
    if (this.resizeStart) {
      this.applyResize(event);
      return;
    }
    if (!this.dragOrigin || !this.dragTarget) {
      return;
    }

    const travelled =
      Math.abs(event.clientX - this.dragOrigin.x) + Math.abs(event.clientY - this.dragOrigin.y);
    if (!this.dragging && travelled < DRAG_THRESHOLD) {
      return;
    }

    this.dragging = true;
    this.dragPos = {
      x: event.clientX - this.dragOffset.x,
      y: event.clientY - this.dragOffset.y,
    };
    this.cdr.markForCheck();
  }

  private handlePointerUp(event: PointerEvent): void {
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);

    if (this.resizeStart) {
      this.resizeStart = null;
      return;
    }

    if (this.dragging && this.dragTarget) {
      if (this.dragTarget === 'bubble') {
        this.shell.setCorner(this.nearestCorner(event.clientX, event.clientY));
      } else {
        this.shell.setEdge(this.nearestEdge(event.clientX));
      }
    }

    this.dragPos = null;
    this.dragOrigin = null;
    this.dragTarget = null;
    // Cleared on the next frame so the click that follows pointerup is still suppressed.
    setTimeout(() => {
      this.dragging = false;
      this.cdr.markForCheck();
    });
    this.cdr.markForCheck();
  }

  private nearestCorner(x: number, y: number): BubbleCorner {
    const vertical = y + BUBBLE_SIZE / 2 < window.innerHeight / 2 ? 'top' : 'bottom';
    const horizontal = x + BUBBLE_SIZE / 2 < window.innerWidth / 2 ? 'left' : 'right';
    return `${vertical}-${horizontal}` as BubbleCorner;
  }

  private nearestEdge(x: number): PanelEdge {
    return x < window.innerWidth / 2 ? 'left' : 'right';
  }

  // ------------------------------------------
  // Resize — clamped to 260-420 x 400-900
  // ------------------------------------------

  startResize(event: PointerEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.resizeStart = {
      x: event.clientX,
      y: event.clientY,
      width: this.placement.width,
      height: this.placement.height,
    };
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
  }

  private applyResize(event: PointerEvent): void {
    if (!this.resizeStart) {
      return;
    }
    // Dragging the handle away from the docked edge grows the panel.
    const dx = event.clientX - this.resizeStart.x;
    const dy = event.clientY - this.resizeStart.y;
    const widthDelta = this.placement.edge === 'right' ? -dx : dx;
    this.shell.setSize(this.resizeStart.width + widthDelta, this.resizeStart.height - dy);
  }

  private clearWakeTimer(): void {
    if (this.wakeTimer !== null) {
      clearTimeout(this.wakeTimer);
      this.wakeTimer = null;
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    this.clearWakeTimer();
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
